import { authedFetch } from './backendFetch';
import { canReviewOcr, attachmentOcrRequest } from './attachmentOcr';
import { isDigitalDocumentSource, loadDigitalDocumentSource } from './digitalDocumentSource';
import { hasReportInputText } from './reportInput';

export const sourceLimits={maxSources:20,maxPdfPages:50,maxCombinedPages:100,maxBytes:15*1024*1024,maxText:100000};
const abort=()=>Object.assign(new Error('Source preparation cancelled.'),{name:'AbortError'});
const check=signal=>{if(signal?.aborted) throw abort();};

export function selectionSummary(sources,rows){
 const problems=[];let pageCount=0,textLength=0,readyCount=0,unknownPages=0,bytes=0;
 if(sources.length>sourceLimits.maxSources) problems.push('Select up to 20 sources.');
 for(const source of sources){
  const row=rows[source.id]||{};
  if(row.status==='ready') readyCount++;
  if(row.kind==='pdf'&&row.pageCount>sourceLimits.maxPdfPages) problems.push(`${source.title} exceeds 50 pages.`);
  if(row.kind==='docx') { /* One logical Word document, not a physical page. */ }
  else if(Number.isSafeInteger(row.pageCount)) pageCount+=row.pageCount;
  else if(canReviewOcr(source)) unknownPages++;
  if(row.bytes>sourceLimits.maxBytes) problems.push(`${source.title} exceeds 15 MiB.`);
  if(canReviewOcr(source)&&Number.isSafeInteger(row.bytes)) bytes+=row.bytes;
  // Reserve room for the report builder's source labels and separators.
  if(Number.isSafeInteger(row.textLength)) textLength+=row.textLength+(source.title?.length||0)+256;
 }
 if(pageCount>sourceLimits.maxCombinedPages) problems.push('Selected PDFs and images exceed 100 pages.');
 if(bytes>sourceLimits.maxBytes) problems.push('Selected Word, PDF and image sources exceed 15 MiB in total.');
 if(textLength>sourceLimits.maxText) problems.push('The selected sources contain too much text for one report (100,000 characters). Remove some sources.');
 return {sourceCount:sources.length,pageCount,readyCount,unknownPages,textLength,bytes,problems,
  ready:sources.length>0&&readyCount===sources.length&&!problems.length};
}

async function sourceRequest(path,signal){
 const base=(process.env.REACT_APP_MAIN_BACKEND_URL||'').replace(/\/$/,'');
 const response=await authedFetch(base+path,{signal,cache:'no-store'});
 const data=await response.json().catch(()=>({}));
 if(!response.ok) throw new Error(data.error||'Source status could not be loaded.');
 return data;
}

export const inspectSource=(source,signal)=>sourceRequest('/scribe/attachments/inspect?'+new URLSearchParams({path:source.path}),signal);
export const pollSource=(jobId,source,signal)=>jobId
 ? sourceRequest('/scribe/attachments/ocr/jobs/'+encodeURIComponent(jobId),signal)
 : attachmentOcrRequest('GET',source.path,{},signal);

export async function loadSourceText(source,retrieveFile){
 if(isDigitalDocumentSource(source)) return (await loadDigitalDocumentSource(source,retrieveFile)).text;
 if(source.type==='Consultation') return (source.originalData?.transcription||'')+'\n'+(source.originalData?.notes||'');
 if(source.type==='Document') return source.originalData?.content||'';
 throw new Error('This file is not supported for report generation.');
}

const waitForPoll=signal=>new Promise((resolve,reject)=>{
 check(signal);
 const cancel=()=>{clearTimeout(timer);reject(abort());};
 const timer=setTimeout(()=>{signal?.removeEventListener('abort',cancel);resolve();},3000);
 signal?.addEventListener('abort',cancel,{once:true});
});

export async function prepareSelectedSources(sources,options){
 const {signal,onUpdate,inspect=inspectSource,
  start=(source)=>attachmentOcrRequest('POST',source.path,{},signal),
  poll=(job,source)=>pollSource(job,source,signal),wait=()=>waitForPoll(signal),
  save=(source,result)=>attachmentOcrRequest('PATCH',source.path,{id:result.id,revision:result.revision,pages:result.pages.map(p=>p.text)},signal),
  loadText=source=>loadSourceText(source,options.retrieveFile)}=options;
 const rows={};
 const update=(source,row)=>{check(signal);rows[source.id]={...rows[source.id],...row};onUpdate(source.id,rows[source.id]);};
 const initial=selectionSummary(sources,rows);
 if(initial.problems.length) throw new Error(initial.problems.join(' '));
 check(signal);
 // Inspect every source before spending on OCR. Bounded reads also avoid
 // issuing twenty authenticated downloads at once on the shared backend.
 for(const source of sources){
  update(source,{status:'checking'});
  try{
   if(canReviewOcr(source)){
    const meta=await inspect(source,signal);check(signal);
    if(!['pdf','image','docx'].includes(meta.kind)||!Number.isSafeInteger(meta.pageCount)||meta.pageCount<1||!Number.isSafeInteger(meta.bytes)||meta.bytes<0) throw new Error('Source metadata could not be verified.');
    if(meta.pageCount>20&&!meta.backgroundAvailable&&meta.status!=='ready') throw new Error('Background preparation is not configured in this environment. Ask an administrator to complete the backend setup.');
    update(source,{...meta,status:meta.status==='ready'&&meta.reviewed_pages?'ready':'pending',
     textLength:meta.reviewed_pages?.reduce((n,t)=>n+t.length,0)});
   }else{
    const text=await loadText(source);check(signal);
    if(!hasReportInputText(text)) throw new Error('This source has no readable text.');
    update(source,{status:'ready',textLength:text.length});
   }
  }catch(error){if(error.name==='AbortError') throw error;update(source,{status:'failed',message:error.message});}
 }
 const inspected=selectionSummary(sources,rows);
 if(inspected.problems.length) throw new Error(inspected.problems.join(' '));
 if(Object.values(rows).some(row=>row.status==='failed')) return {...inspected,ready:false,rows};
 const results={};
 // Persist all eligible jobs before waiting for any provider work. Requests are
 // serial and the queue bounds actual processing across browser sessions.
 for(const source of sources){
  check(signal);
  if(rows[source.id].status==='ready') continue;
  try{
   const result=await start(source);check(signal);
   results[source.id]=result;
   update(source,{...result,status:result.stage==='queued'?'queued':result.status});
  }catch(error){if(error.name==='AbortError') throw error;update(source,{status:'failed',message:error.message});}
 }
 for(const source of sources){
  check(signal);
  if(!results[source.id]) continue;
  try{
   let result=results[source.id];
   const jobId=result.jobId;
   // A background job persists after the browser closes; this loop only
   // reads its progress. Reselecting or unmounting cancels these reads.
   while(result.status==='processing'){
    await wait(signal);check(signal);
    result=await poll(jobId,source,signal);check(signal);
    update(source,{...result,status:result.stage==='queued'?'queued':result.status});
   }
   if(result.status!=='ready'||!Array.isArray(result.pages)||!result.pages.length) throw new Error(result.message||'This source could not be prepared. Retry it or remove it.');
   if(!result.reviewed_pages) result=await save(source,result);
   if(!Array.isArray(result.reviewed_pages)||!result.reviewed_pages.some(text=>text.trim())) throw new Error('Source text has not been saved yet.');
   update(source,{...result,status:'ready',textLength:result.reviewed_pages.reduce((n,text)=>n+text.length,0)});
  }catch(error){if(error.name==='AbortError') throw error;update(source,{status:'failed',message:error.message});}
 }
 return {...selectionSummary(sources,rows),rows};
}

export function reportSourceManifest(sources,selectedIds=sources.map(source=>source.id)){
 if(sources.some(source=>source.sourceError||!['Attachment','Consultation','Document'].includes(source.type)))
  throw new Error('A report source is not ready. Reload or prepare it before generating.');
 if(sources.length!==selectedIds.length||sources.some((source,index)=>source.id!==selectedIds[index]))
  throw new Error('The source selection changed. Prepare the sources again before generating.');
 return sources.map(source=>source.type==='Attachment'
  ? {kind:'attachment',reference:source.path}
  : {kind:source.type==='Consultation'?'consultation':'report',id:source.id});
}
